"use client";

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu";

interface MultiSelectDropdownProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  showSelectAll?: boolean;
}

export function MultiSelectDropdown({
  options,
  value = [],
  onChange,
  placeholder = "Select options...",
  className = "",
  disabled = false,
  loading = false,
  showSelectAll = false,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  // Position dropdown when opening (before paint) and on scroll/resize
  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
        setIsOpen(false);
        setSearchQuery(""); // Reset search when closing
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search query
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (option: string) => {
    if (disabled) return;
    
    const newValue = value.includes(option)
      ? value.filter((v) => v !== option)
      : [...value, option];
    onChange(newValue);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    
    if (value.length === options.length) {
      // Deselect all
      onChange([]);
    } else {
      // Select all
      onChange([...options]);
    }
  };

  const allSelected = options.length > 0 && value.length === options.length;

  const displayText = value.length > 0 ? `${value.length} selected` : placeholder;


  return (
    <div ref={triggerRef} className={cn("relative w-full", className)}>
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className={cn(
          "w-full rounded-md border px-3 py-2 text-left text-sm",
          "border-white/10 bg-white/5 text-white",
          "focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "flex items-center justify-between gap-2"
        )}
      >
        <span className={cn(
          "truncate",
          value.length === 0 && "text-white/60"
        )}>
          {loading ? "Loading..." : displayText}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-white/60 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {value.length > 0 && !isOpen && (
        <div className="mt-2 flex flex-wrap gap-1">
          {value.slice(0, 3).map((option) => (
            <span
              key={option}
              className="inline-flex items-center gap-1 rounded bg-[#4da3ff]/20 px-2 py-0.5 text-xs text-[#4da3ff]"
            >
              {option}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((v) => v !== option));
                }}
                className="hover:text-[#4da3ff]/80"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {value.length > 3 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center rounded bg-white/10 px-2 py-0.5 text-xs text-white/60 hover:bg-white/15"
                  onClick={(e) => e.stopPropagation()}
                >
                  +{value.length - 3} more
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="max-h-72 w-72 overflow-y-auto border-white/10 bg-black/95 text-white"
              >
                {value.map((selectedValue) => (
                  <DropdownMenuItem
                    key={selectedValue}
                    onSelect={(e) => e.preventDefault()}
                    className="cursor-default focus:bg-white/10"
                  >
                    <span className="truncate">{selectedValue}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {isOpen && !loading && options.length > 0 && dropdownPosition.width > 0 && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] rounded-md border border-white/10 bg-black/95 shadow-lg"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: "320px",
            }}
          >
            {/* Search Bar */}
            <div className="p-2 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Search options..."
                  className={cn(
                    "w-full pl-8 pr-3 py-1.5 text-sm rounded-md",
                    "border border-white/10 bg-white/5 text-white",
                    "placeholder:text-white/40",
                    "focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                  )}
                />
              </div>
            </div>
            <div
              className="overflow-y-auto overflow-x-hidden p-2"
              style={{
                maxHeight: "260px",
                overscrollBehavior: "contain",
              }}
            >
              {showSelectAll && filteredOptions.length > 0 && (
                <div
                  className={cn(
                    "flex items-center space-x-2 cursor-pointer rounded px-2 py-1.5 mb-1",
                    "hover:bg-white/10 transition-colors border-b border-white/10",
                    "select-none"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    readOnly
                    tabIndex={-1}
                    className="h-4 w-4 rounded border-white/10 bg-white/5 text-[#4da3ff] focus:ring-[#4da3ff] pointer-events-none"
                  />
                  <span className="text-sm font-medium text-white flex-1">
                    Select All
                  </span>
                </div>
              )}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option);
                  return (
                    <div
                      key={option}
                      className={cn(
                        "flex items-center space-x-2 cursor-pointer rounded px-2 py-1.5",
                        "hover:bg-white/10 transition-colors",
                        "select-none"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(option);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        tabIndex={-1}
                        className="h-4 w-4 rounded border-white/10 bg-white/5 text-[#4da3ff] focus:ring-[#4da3ff] pointer-events-none"
                      />
                      <span className="text-sm text-white flex-1">
                        {option}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="px-2 py-4 text-center text-sm text-white/60">
                  No options found
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

